import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function ManageSection() {

  const [data,    setData]    = useState([]);
  const [page,    setPage]    = useState(1);
  const [limit,   setLimit]   = useState(null);  // null = not loaded yet
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [editId,  setEditId]  = useState(null);

  const [form, setForm] = useState({
    section_name: '',
    heading:      '',
    subheading:   ''
  });

  /* ─────────────────────────────────────────
     Step 1: Fetch page limit from tbl_settings
     (parid=1 → page limit row)
     Runs ONCE on mount
  ───────────────────────────────────────── */
  useEffect(() => {
    API.get('/settings/page-limit')
      .then(res => setLimit(Number(res.data.limit) || 10))
      .catch(()  => setLimit(10)); // fallback default
  }, []);

  /* ─────────────────────────────────────────
     Step 2: Fetch sections only AFTER limit
     is loaded from DB
  ───────────────────────────────────────── */
  useEffect(() => {
    if (limit === null) return; // wait for limit
    fetchData();
  }, [page, limit]);

  const fetchData = () => {
    setLoading(true);
    API.get(`/sections?page=${page}&limit=${limit}`)
      .then(res => {
        setData(res.data?.data   || []);
        setTotal(res.data?.total || 0);
      })
      .catch(err => {
        console.error("FETCH ERROR:", err.response?.data || err.message);
        toast.error("Failed to load data");
        setData([]);
      })
      .finally(() => setLoading(false));
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    if (!form.section_name || !form.heading) {
      toast.warning("Section Name and Heading are required");
      return;
    }
    try {
      if (editId) {
        await API.put(`/sections/${editId}`, form);
        toast.success("Updated successfully");
      } else {
        await API.post('/sections', form);
        toast.success("Added successfully");
      }
      setForm({ section_name: '', heading: '', subheading: '' });
      setEditId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || "Something went wrong");
    }
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure to delete this record?")) return;
    if (!window.confirm("Are you sure to delete the record!!!")) return;
    try {
      await API.delete(`/sections/${id}`);
      toast.success("Deleted successfully");
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ── Status Toggle ── */
  const toggleStatus = async (row) => {
    const newStatus = row.section_status === 1 ? 0 : 1;
    const msg = newStatus === 0
      ? "Are you Sure to Inactive The Status ?"
      : "Are you Sure to Active The Status ?";
    if (!window.confirm(msg)) return;
    try {
      await API.put(`/sections/status/${row.id}`, { status: newStatus });
      fetchData();
    } catch {
      toast.error("Status update failed");
    }
  };

  /* ── Cancel Edit ── */
  const handleCancel = () => {
    setForm({ section_name: '', heading: '', subheading: '' });
    setEditId(null);
  };

  const totalPages = limit ? Math.ceil(total / limit) : 1;

  /* ── Wait for limit to load ── */
  if (limit === null) {
    return (
      <Layout activePage="Section">
        <div style={S.emptyCell}>Loading settings...</div>
      </Layout>
    );
  }

  return (
    <Layout activePage="Section">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* TOP BAR */}
      <div style={S.topbar}>
        <div>
          <h2 style={S.title}>Manage Sections</h2>
          <p style={S.subtitle}>Configure homepage section content</p>
        </div>
      </div>

      {/* FORM */}
      <div style={S.formCard}>
        <div style={S.formTitle}>
          {editId ? '✏️ Edit Section' : '➕ Add New Section'}
        </div>

        <div style={S.formGrid}>
          <div style={S.fg}>
            <label style={S.label}>Section Name <span style={S.req}>*</span></label>
            <select
              value={form.section_name}
              onChange={e => setForm({ ...form, section_name: e.target.value })}
              style={S.input}
            >
              <option value="">---Select---</option>
              <option value="why choose us">why choose us</option>
              <option value="What is included">What is included</option>
              <option value="Car Rental Location">Car Rental Location</option>
            </select>
          </div>

          <div style={S.fg}>
            <label style={S.label}>Heading <span style={S.req}>*</span></label>
            <input
              placeholder="Enter heading..."
              value={form.heading}
              onChange={e => setForm({ ...form, heading: e.target.value })}
              style={S.input}
            />
          </div>

          <div style={S.fg}>
            <label style={S.label}>Sub Heading</label>
            <input
              placeholder="Enter sub heading..."
              value={form.subheading}
              onChange={e => setForm({ ...form, subheading: e.target.value })}
              style={S.input}
            />
          </div>
        </div>

        <div style={S.formActions}>
          {editId && (
            <button style={S.cancelBtn} onClick={handleCancel}>Clear</button>
          )}
          <button style={S.submitBtn} onClick={handleSubmit}>
            {editId ? '💾 Update' : '✚ Submit'}
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div style={S.card}>
        {loading ? (
          <div style={S.emptyCell}>Loading...</div>
        ) : data.length === 0 ? (
          <div style={{ ...S.emptyCell, color: 'red' }}>-- No records Available --</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={S.th}>#</th>
                <th style={S.th}>Section Name</th>
                <th style={S.th}>Heading</th>
                <th style={S.th}>Sub Heading</th>
                <th style={S.th}>Edit</th>
                <th style={S.th}>Delete</th>
                <th style={S.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={row.id}>
                  <td style={S.td}>
                    <span style={S.serial}>{(page - 1) * limit + i + 1}</span>
                  </td>
                  <td style={S.td}>
                    <span style={S.groupChip}>{row.section_name}</span>
                  </td>
                  <td style={S.td}>{row.heading}</td>
                  <td style={S.td}>{row.subheading || '—'}</td>
                  <td style={S.td}>
                    <button
                      style={S.editBtn}
                      onClick={() => {
                        setForm({
                          section_name: row.section_name,
                          heading:      row.heading,
                          subheading:   row.subheading || ''
                        });
                        setEditId(row.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    >
                      Edit
                    </button>
                  </td>
                  <td style={S.td}>
                    <button style={S.delBtn} onClick={() => handleDelete(row.id)}>
                      Delete
                    </button>
                  </td>
                  <td style={S.td}>
                    <button
                      style={row.section_status === 1 ? S.activeBadge : S.inactiveBadge}
                      onClick={() => toggleStatus(row)}
                    >
                      {row.section_status === 1 ? '● Active' : '○ Inactive'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* PAGINATION */}
        <div style={S.pagination}>
          <button
            style={{ ...S.pageBtn, opacity: page === 1 ? 0.5 : 1 }}
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            ◀ Prev
          </button>

          <span style={S.pageInfo}>
            Page {page} of {totalPages || 1}
            <span style={{ color: '#aaa', fontWeight: 400, marginLeft: 8 }}>
              ({total} total)
            </span>
          </span>

          <button
            style={{ ...S.pageBtn, opacity: page >= totalPages ? 0.5 : 1 }}
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next ▶
          </button>
        </div>
      </div>
    </Layout>
  );
}


/* ─── Styles ─── */
const S = {
  topbar:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:    { margin: 0, fontSize: 26, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#999' },

  formCard:    { background: '#eef0f5', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', padding: '20px 24px', marginBottom: 20 },
  formTitle:   { fontSize: 14, fontWeight: 700, color: '#444', marginBottom: 16 },
  formGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 16 },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  fg:          { display: 'flex', flexDirection: 'column', gap: 5 },
  label:       { fontSize: 12, fontWeight: 600, color: '#555' },
  req:         { color: '#ff6b6b' },
  input: {
    padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 8,
    fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none',
    width: '100%', boxSizing: 'border-box', background: '#fff'
  },
  submitBtn: {
    padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)',
    color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 700, fontFamily: 'inherit'
  },
  cancelBtn: {
    padding: '9px 20px', background: '#f5f5f5', color: '#666',
    border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit'
  },

  card:      { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  theadRow:  { background: '#fafafa' },
  th: {
    padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#999',
    textAlign: 'center', borderBottom: '2px solid #f0f0f0',
    textTransform: 'uppercase', letterSpacing: '0.5px'
  },
  td:        { padding: '12px 14px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8', textAlign: 'center' },
  emptyCell: { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },

  serial:    { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  groupChip: { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 },

  editBtn: {
    background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  delBtn: {
    background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  activeBadge: {
    background: '#f0fff6', color: '#1a9e5c', border: '1px solid #b8f0d0',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },
  inactiveBadge: {
    background: '#fff3f0', color: '#d45700', border: '1px solid #ffd0b0',
    padding: '4px 12px', borderRadius: 20, cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit'
  },

  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 20px', borderTop: '1px solid #f0f0f0'
  },
  pageBtn: {
    padding: '6px 14px', border: 'none', borderRadius: 8,
    background: '#6C63FF', color: '#fff', cursor: 'pointer',
    fontFamily: 'inherit', fontWeight: 600
  },
  pageInfo: { fontSize: 14, fontWeight: 600 },
};